import React, { useState, useEffect, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HelpGuideButton from "../../HelpGuideButton";
import { useTranslation } from "../../../../i18n/LanguageContext";
import {
  faEdit,
  faSearch,
  faChevronDown,
  faChevronUp,
  faClock,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faArrowLeft,
  faChartBar,
  faTrash,
  faRedo,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import AnalysisInput from "./components/AnalysisInput";
import AnalysisDetails from "./components/AnalysisDetails";
import SocialMediaAnalysisAPI from "../../services/SocialMediaAnalysisAPI";
import { useUser } from "../../context/UserContext";
import { useLoading } from "../../../../contexts/LoadingContext";
import { formatDateOnly } from "../../utils/DateTimeUtil";
import "../../css/Common.css";
import "../text/css/SmsAnalysisSimpleMobile.css";
import "./css/SocialMediaAnalysisMobile.css";
import DisclosuresModal from "../common/DisclosuresModal";
import LoadingSpinner from "../../common/LoadingSpinner";
import ScamReportAPI from "../../services/ScamReportAPI";

// ─────────────────────────────────────────────
// Accordion Section
// ─────────────────────────────────────────────
const AccordionSection = ({
  title,
  icon,
  children,
  defaultOpen = false,
  badge,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`smam-accordion ${open ? "smam-accordion--open" : ""}`}>
      <button
        type="button"
        className="smam-accordion-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="smam-accordion-title">
          {icon && (
            <FontAwesomeIcon icon={icon} className="smam-accordion-icon" />
          )}
          {title}
          {badge && <span className="smam-accordion-badge">{badge}</span>}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
      </button>
      {open && <div className="smam-accordion-body">{children}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────
// Risk helpers
// ─────────────────────────────────────────────
const isSystemError = (item) => {
  if (!item || !item.explanation) return false;
  return (
    item.explanation.includes("SYSTEM_ERROR") ||
    item.explanation.includes(
      "Analysis was unavailable due to processing error",
    ) ||
    item.explanation.includes("Unable to analyze") ||
    (item.confidence === 0 && item.explanation.includes("technical error"))
  );
};

const hasInsufficientData = (item) => {
  if (!item) return false;
  return (
    item.dataSufficiency === "INSUFFICIENT" ||
    item.explanation?.includes("Insufficient Data") ||
    item.explanation?.includes("insufficient data") ||
    item.explanation?.includes("Not enough reliable information") ||
    (item.confidence && item.confidence < 0.4)
  );
};

const getRiskBadgeText = (item, t) => {
  if (isSystemError(item)) return t("social.detail.analysisUnavailable");
  if (item.analysisStatus) return item.analysisStatus;
  if (hasInsufficientData(item)) return t("social.detail.insufficientData");
  return item.isScam
    ? t("social.detail.potentialScam")
    : t("social.detail.appearsLegitimate");
};

const getStatusClass = (item) => {
  if (isSystemError(item)) return "system-error";
  if (item.analysisStatus) {
    const s = item.analysisStatus.toLowerCase();
    if (s.includes("very likely") && s.includes("scam"))
      return "very-high-risk";
    if (s.includes("potentially") && s.includes("scam"))
      return hasInsufficientData(item)
        ? "medium-risk-insufficient"
        : "high-risk";
    if (s.includes("unlikely") && s.includes("scam"))
      return hasInsufficientData(item) ? "low-risk-insufficient" : "low-risk";
    if (s.includes("legitimate")) return "low-risk";
  }
  if (item.conclusion) {
    const c = item.conclusion.toLowerCase();
    if (c.includes("very likely") && c.includes("scam"))
      return "very-high-risk";
    if (c.includes("likely") && c.includes("scam")) return "high-risk";
    if (c.includes("potentially") && c.includes("scam")) return "high-risk";
    if (c.includes("unlikely") && c.includes("scam")) return "low-risk";
    if (c.includes("legitimate") || c.includes("safe") || c.includes("trusted"))
      return "low-risk";
  }
  if (hasInsufficientData(item)) return "insufficient-data";
  if (typeof item.isScam === "boolean")
    return item.isScam ? "high-risk" : "low-risk";
  return "unknown-risk";
};

const getStatusIcon = (item) => {
  if (isSystemError(item)) return faExclamationTriangle;
  if (item.analysisStatus) {
    const s = item.analysisStatus.toLowerCase();
    if (s.includes("very likely") && s.includes("scam")) return faTimesCircle;
    if (s.includes("potentially") && s.includes("scam"))
      return faExclamationTriangle;
    if (s.includes("unlikely") && s.includes("scam")) return faCheckCircle;
    if (s.includes("legitimate")) return faCheckCircle;
  }
  if (item.conclusion) {
    const c = item.conclusion.toLowerCase();
    if (c.includes("very likely") && c.includes("scam")) return faTimesCircle;
    if (c.includes("likely") && c.includes("scam")) return faTimesCircle;
    if (c.includes("potentially") && c.includes("scam"))
      return faExclamationTriangle;
    if (c.includes("unlikely") && c.includes("scam")) return faCheckCircle;
    if (c.includes("legitimate") || c.includes("safe") || c.includes("trusted"))
      return faCheckCircle;
  }
  if (hasInsufficientData(item)) return faExclamationTriangle;
  if (typeof item.isScam === "boolean")
    return item.isScam ? faTimesCircle : faCheckCircle;
  return faExclamationTriangle;
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const SocialMediaAnalysisMobile = () => {
  const { loggedInUser } = useUser();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();
  const loggedInUserUuid = loggedInUser?.uuid || null;

  // Slide state
  const [activeSlide, setActiveSlide] = useState("form");
  const containerRef = useRef(null);

  const scrollTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const showResults = useCallback(() => {
    setActiveSlide("results");
    scrollTop();
  }, [scrollTop]);

  const showForm = useCallback(() => {
    setActiveSlide("form");
    scrollTop();
  }, [scrollTop]);

  // Core form states
  const [formData, setFormData] = useState({
    businessName: "",
    websiteUrl: "",
    merchandiseType: "",
    businessInteractionReason: "",
    businessContactName: "",
    contactPhone: "",
    socialMediaPlatform: "",
    userContext: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [analysisResult, setAnalysisResult] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [reportIdMap, setReportIdMap] = useState({});
  const [error, setError] = useState(null);

  // History management
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  // Load analysis history
  const fetchAnalysisHistory = useCallback(async () => {
    if (!loggedInUserUuid) return;
    setHistoryLoading(true);
    try {
      const history =
        await SocialMediaAnalysisAPI.getAnalysisHistory(loggedInUserUuid);
      const historySlice = history.slice(0, 5);
      setAnalysisHistory(historySlice);

      // Look up reportIds for scam-detected history items
      const scamItems = historySlice.filter(
        (item) => item.isScam === true && item.database_id,
      );
      if (scamItems.length > 0) {
        try {
          const sourceIds = scamItems.map((item) => item.database_id);
          const lookupResult = await ScamReportAPI.lookupReportIdsBySource(
            "SOCIAL_MEDIA_ANALYSIS",
            sourceIds,
          );
          if (lookupResult?.mapping) {
            setReportIdMap(lookupResult.mapping);
          }
        } catch (lookupErr) {
          console.error(
            "Failed to look up social media report IDs:",
            lookupErr,
          );
        }
      }
    } catch (err) {
      console.error(
        "Error loading analysis history:",
        err.message || err.toString(),
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [loggedInUserUuid]);

  useEffect(() => {
    if (loggedInUserUuid) fetchAnalysisHistory();
  }, [loggedInUserUuid, fetchAnalysisHistory]);

  // Auto-slide to results when analysis completes
  const prevResultRef = useRef(analysisResult);
  if (analysisResult && analysisResult !== prevResultRef.current) {
    prevResultRef.current = analysisResult;
    if (activeSlide === "form") {
      setTimeout(() => {
        setActiveSlide("results");
        scrollTop();
      }, 100);
    }
  }

  const validateForm = () => {
    const errors = {};
    if (!formData.businessName.trim() && !formData.websiteUrl.trim()) {
      errors.businessName = t("social.form.error.nameOrUrl");
      errors.websiteUrl = t("social.form.error.nameOrUrl");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!loggedInUserUuid) {
      setError(t("social.form.error.notLoggedIn"));
      return;
    }

    showLoading(
      "Analyzing Social Media Interaction",
      "AI is researching this business. This may take 30-60 seconds...",
    );
    setError(null);
    setAnalysisResult(null);
    setSelectedHistoryItem(null);

    try {
      const normalizeUrl = (url) => {
        if (!url || !url.trim()) return url;
        const t = url.trim();
        return t.startsWith("http://") || t.startsWith("https://")
          ? t
          : `https://${t}`;
      };

      const normalizedFormData = {
        ...formData,
        websiteUrl: normalizeUrl(formData.websiteUrl),
        userUuid: loggedInUserUuid,
      };

      const result = await SocialMediaAnalysisAPI.analyzeSocialMediaScam(
        normalizedFormData,
        {
          pollInterval: 3000,
          maxPolls: 30,
          onStatusUpdate: (status, pollCount) => {
            if (pollCount > 5) {
              showLoading(
                "Analyzing Social Media Interaction",
                "AI is performing deep analysis with web search. Almost done...",
              );
            }
          },
        },
      );

      const transformedResult = {
        ...result,
        businessName: normalizedFormData.businessName,
        merchandiseType: normalizedFormData.merchandiseType,
        platform: normalizedFormData.socialMediaPlatform,
        contactName: normalizedFormData.businessContactName,
        interactionReason: normalizedFormData.businessInteractionReason,
        websiteUrl: normalizedFormData.websiteUrl,
        confidence: result.confidence,
      };

      setAnalysisResult(transformedResult);

      // Save report on detection — match AnalysisDetails share button logic
      const statusText = (transformedResult.analysisStatus || "").toLowerCase();
      const isThreatDetected =
        transformedResult.isScam === true ||
        ((statusText.includes("very likely") ||
          statusText.includes("potentially")) &&
          statusText.includes("scam") &&
          !statusText.includes("insufficient"));
      if (isThreatDetected) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "SOCIAL_MEDIA_ANALYSIS",
            analysisTitle: `Scam Detected — ${transformedResult.businessName || transformedResult.websiteUrl || "Unknown Business"}`,
            analysisDetail:
              transformedResult.analysisStatus || "Social media scam detected",
            analysisInput:
              transformedResult.websiteUrl ||
              transformedResult.businessName ||
              "",
            context: formData.userContext?.trim() || undefined,
            isScam: true,
            confidence: transformedResult.confidence || 0,
            riskLevel: transformedResult.riskLevel || "high",
            explanation: transformedResult.explanation || "",
            suggestions: transformedResult.suggestions || "",
            sourceAnalysisId: result.database_id || null,
            sourceAnalysisType: "SOCIAL_MEDIA_ANALYSIS",
          });
          if (saveResponse.success) {
            setReportId(saveResponse.reportId);
          }
        } catch (saveErr) {
          console.error(
            "[SocialMediaAnalysisMobile] Failed to save report:",
            saveErr,
          );
        }
      }
      setSelectedHistoryItem(null);
      await fetchAnalysisHistory();
    } catch (err) {
      console.error("Analysis failed:", err.message || err.toString());
      setError(err.message || "Analysis failed. Please try again.");
    } finally {
      hideLoading();
    }
  };

  const handleHistoryItemSelect = (item) => {
    setSelectedHistoryItem(item);
    setAnalysisResult(null);
    showResults();
  };

  const handleDeleteHistoryItem = async (item) => {
    try {
      await SocialMediaAnalysisAPI.deleteAnalysis(item.id);
      setAnalysisHistory((prev) => prev.filter((h) => h.id !== item.id));
      if (selectedHistoryItem?.id === item.id) {
        setSelectedHistoryItem(null);
        setAnalysisResult(null);
      }
    } catch (err) {
      console.error("Error deleting analysis:", err.message || err.toString());
      setError(t("social.form.error.deleteFailed"));
    }
  };

  const handleReset = () => {
    setFormData({
      businessName: "",
      websiteUrl: "",
      merchandiseType: "",
      businessInteractionReason: "",
      businessContactName: "",
      contactPhone: "",
      socialMediaPlatform: "",
      userContext: "",
    });
    setFormErrors({});
    setError(null);
    setAnalysisResult(null);
    setSelectedHistoryItem(null);
  };

  const handleNewAnalysis = () => {
    handleReset();
    showForm();
  };

  // ─── Current result helper ───
  const currentResult = analysisResult || selectedHistoryItem;

  // ─── RENDER ───
  return (
    <div className="smam-container" ref={containerRef}>
      {/* Header */}
      <div className="sasm-header">
        <div className="sasm-header-top">
          <h1 className="main-title">{t("social.page.title")}</h1>
          <HelpGuideButton guideId={7} />
        </div>
        <p className="common-subtitle">{t("social.page.subtitleMobile")}</p>
      </div>

      {/* Slide Wrapper */}
      <div
        className={`smam-view-slider ${
          activeSlide === "results" ? "smam-slide-results" : ""
        }`}
      >
        {/* ── SLIDE 1: Form + Recent History ── */}
        <div className="smam-slide smam-slide-form">
          <div className="smam-form-wrapper">
            {/* Section Header */}
            <div className="smam-section-header">
              <FontAwesomeIcon icon={faEdit} className="smam-section-icon" />
              <span>{t("social.section.inputs")}</span>
            </div>

            {/* Error Banner */}
            {error && (
              <div className="smam-error-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            {/* Reuse AnalysisInput sub-component */}
            <AnalysisInput
              formData={formData}
              setFormData={setFormData}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              error={null}
              onSubmit={handleSubmit}
              onReset={handleReset}
              isMobile={true}
              isOptimized={true}
            />

            {/* Results banner when results are available */}
            {currentResult && (
              <button
                type="button"
                className="smam-results-banner"
                onClick={showResults}
              >
                <FontAwesomeIcon icon={faChartBar} />
                <span>{t("social.viewResults")}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="smam-banner-arrow"
                  style={{ transform: "rotate(-90deg)" }}
                />
              </button>
            )}

            {/* Recent History Accordion */}
            <AccordionSection
              title={t("social.history.recentTitle")}
              icon={faClock}
              badge={
                analysisHistory.length > 0
                  ? String(analysisHistory.length)
                  : null
              }
              defaultOpen={false}
            >
              {historyLoading ? (
                <LoadingSpinner
                  message={t("social.history.loading")}
                  size="md"
                  className="compact"
                />
              ) : analysisHistory.length === 0 ? (
                <div className="smam-empty-history">
                  <p>{t("social.history.empty")}</p>
                  <button
                    type="button"
                    className="smam-btn-link"
                    onClick={fetchAnalysisHistory}
                  >
                    <FontAwesomeIcon icon={faRedo} /> {t("common.refresh")}
                  </button>
                </div>
              ) : (
                <div className="smam-history-list">
                  {analysisHistory.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className={`smam-history-card ${
                        selectedHistoryItem?.id === item.id
                          ? "smam-history-card--active"
                          : ""
                      }`}
                      onClick={() => handleHistoryItemSelect(item)}
                    >
                      <div className="smam-history-card-top">
                        <span className="smam-history-name">
                          {item.businessName ||
                            item.websiteUrl ||
                            t("social.history.unknown")}
                        </span>
                        <button
                          type="button"
                          className="smam-history-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteHistoryItem(item);
                          }}
                          title={t("social.history.deleteTitle")}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                      <div className="smam-history-card-bottom">
                        <span
                          className={`smam-risk-chip smam-risk-chip--${getStatusClass(item)}`}
                        >
                          <FontAwesomeIcon icon={getStatusIcon(item)} />
                          {getRiskBadgeText(item, t)}
                        </span>
                        {item.platform && (
                          <span className="smam-tag smam-tag--platform">
                            {item.platform}
                          </span>
                        )}
                        <span className="smam-history-date">
                          {item.date ? formatDateOnly(item.date, "short") : ""}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </AccordionSection>
          </div>
        </div>

        {/* ── SLIDE 2: Results ── */}
        <div className="smam-slide smam-slide-detail">
          {/* Back bar */}
          <button type="button" className="smam-back-bar" onClick={showForm}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>{t("social.nav.backToForm")}</span>
          </button>

          {currentResult ? (
            <div className="smam-results-wrapper">
              {/* Section Header */}
              <div className="smam-section-header">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="smam-section-icon"
                />
                <span>{t("social.section.results")}</span>
              </div>

              {/* AnalysisDetails sub-component */}
              <div className="smam-details-container">
                <AnalysisDetails
                  analysisResult={analysisResult}
                  selectedHistoryItem={selectedHistoryItem}
                  onPreviousCase={() => {}}
                  onNextCase={() => {}}
                  canNavigatePrevious={() => false}
                  canNavigateNext={() => false}
                  isMobile={true}
                  isOptimized={true}
                  reportId={
                    selectedHistoryItem
                      ? reportIdMap[selectedHistoryItem.database_id] || null
                      : reportId
                  }
                />
              </div>

              {/* New Analysis button */}
              <button
                type="button"
                className="btn btn-primary smam-new-analysis-btn"
                onClick={handleNewAnalysis}
              >
                <FontAwesomeIcon icon={faSearch} />
                {t("social.nav.newAnalysis")}
              </button>
            </div>
          ) : (
            <div className="smam-empty-results">
              <FontAwesomeIcon icon={faChartBar} className="smam-empty-icon" />
              <h3>{t("social.empty.title")}</h3>
              <p>{t("social.empty.description")}</p>
              <button
                type="button"
                className="btn btn-primary smam-new-analysis-btn"
                onClick={showForm}
              >
                <FontAwesomeIcon icon={faSearch} />
                {t("social.nav.startAnalysis")}
              </button>
            </div>
          )}
        </div>
      </div>

      <DisclosuresModal disclaimerText={t("social.disclaimer")} />
    </div>
  );
};

export default SocialMediaAnalysisMobile;
